import React, {useState} from 'react';
import {useTracking} from 'react-tracking';
import {addToast, Anchor, Dialogue, Heading, Text} from '@albert-io/atomic';

import withTracking from 'lib/hocs/withTracking';

import sessionStore from 'client/Session/SessionStore';
import appStore from 'client/AppStore';

import {local} from 'lib/StorageUtil';

const TrackedLink = withTracking(Anchor, {
  onClick: {
    event: 'click',
    type: 'hyperlink',
    content: (e, props) => props.children
  }
});

const helpItems = [
  {
    title: 'Get started',
    items: [
      {
        label: 'Set up your classrooms',
        href: 'https://help.albert.io/en/articles/1884980-teacher-onboarding-getting-started'
      }
    ]
  },
  {
    title: 'Finding content',
    items: [
      {
        label: 'Discover questions to assign',
        href: 'https://help.albert.io/en/articles/1885026-how-do-i-search-for-questions-on-albert'
      }
    ]
  },
  {
    title: 'Results & data',
    items: [
      {
        label: 'View class and student data',
        href: 'https://help.albert.io/en/articles/2682890-how-can-i-view-my-classroom-and-student-data'
      }
    ]
  },
  {
    title: 'Assigning work',
    items: [
      {
        label: 'Assign students practice',
        href: 'https://help.albert.io/en/articles/4942861-how-can-i-assign-practice-on-albert'
      }
    ]
  }
] as HelpSectionProps[];

interface HelpSectionProps {
  title: string;
  items: {label: string; href: string}[];
}

const HelpSection = ({title, items}: HelpSectionProps) => (
  <div className='u-mar-t_3'>
    <Text
      bold
      size='xs'
      emphasis='medium'
      className='u-mar-b_1 u-text-transform_uppercase u-display_block u-font-weight_900'
    >
      {title}
    </Text>
    {items.map((item) => (
      <TrackedLink as='a' href={item.href} target='_blank' rel='noopener noreferrer'>
        {item.label}
      </TrackedLink>
    ))}
  </div>
);

const GetStartedWithAlbert = () => {
  const {Track, trackEvent} = useTracking({
    component: 'get started with albert',
    feature: 'teacher dashboard'
  });
  const userId = sessionStore.getUserId();
  const HIDE_GETTING_STARTED_STORAGE_TOKEN = `${userId}-classesHideGetStarted`;

  const getStartedHidden = () => {
    if (
      local.get(HIDE_GETTING_STARTED_STORAGE_TOKEN) ||
      appStore.routerProps.location.pathname.includes('/archived')
    ) {
      return true;
    }
    return false;
  };

  const [hideGettingStarted, setHideGettingStarted] = useState(getStartedHidden());

  const closeGettingStarted = () => {
    local.set(HIDE_GETTING_STARTED_STORAGE_TOKEN, true);
    setHideGettingStarted(true);
    trackEvent({event: 'click', type: 'x-out'});
    const toastMessage = (
      <span>
        You can still access all of the onboarding articles and resources in the{' '}
        <Anchor
          as='a'
          href='https://help.albert.io/en/'
          target='_blank'
          rel='noopener noreferrer'
          underlined
          inheritColor
        >
          Help Center
        </Anchor>
        .
      </span>
    );
    addToast({
      type: 'default',
      message: toastMessage
    });
  };

  if (hideGettingStarted) {
    return null;
  }

  return (
    <Track>
      <Dialogue
        className='u-mar-t_4 u-width_inherit u-mar-b_8'
        paddingLevel={4}
        handleClose={closeGettingStarted}
      >
        <Heading size='s'>Getting started with Albert</Heading>
        <Dialogue.Body className='u-mar-t_3'>
          <iframe
            allowFullScreen
            className='u-width_100pc u-border_none'
            title='Getting started with Albert video'
            src='https://www.youtube.com/embed/6u82kKUcXsU?si=U7N5x4kt7QWg2OKc'
            style={{aspectRatio: '16 / 9'}}
          />

          <div className='teacher-classes__class-cards-get-started-container__get-started-links u-mar-t_3'>
            {helpItems.map((item) => (
              <HelpSection {...item} />
            ))}
          </div>

          <div className='u-mar-t_6'>
            <Text size='s'>
              Not finding what you need? Search our{' '}
              <TrackedLink
                as='a'
                href='https://help.albert.io/en/collections/521216-teachers'
                target='_blank'
                rel='noopener noreferrer'
              >
                Help Center
              </TrackedLink>{' '}
              to learn more.
            </Text>
          </div>
        </Dialogue.Body>
      </Dialogue>
    </Track>
  );
};

export default GetStartedWithAlbert;
